import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://dorado9.com";
const META_TITLE_MAX_LENGTH = 70;
const META_DESCRIPTION_MAX_LENGTH = 150;
const HOMEPAGE_TITLE = "#1 Digital Marketing Expert in Amritsar | SEO, Meta Ads & Social Media";
const HOMEPAGE_DESCRIPTION = "Dorado9 is a digital marketing and creative agency in Amritsar, Punjab, offering SEO, Google Ads, social media, web development and creative services.";

const ROUTE_METADATA = {
  "/": {
    title: HOMEPAGE_TITLE,
    description: HOMEPAGE_DESCRIPTION,
  },
  "/about": {
    title: "About Dorado9 Digital Marketing Agency",
    description: "Learn how Dorado9 combines digital marketing, creative strategy, technology and design to help ambitious businesses grow online.",
  },
  "/services": {
    title: "Digital Marketing Services",
    description: "Explore SEO, Google Ads, social media, web development and creative services built to help businesses grow with clarity and confidence.",
  },
  "/services/seo": {
    title: "SEO Services in Amritsar & Punjab",
    description: "Improve Google rankings and organic traffic with technical SEO, local SEO, keyword research and content optimisation for your business.",
  },
  "/services/paid-ads": {
    title: "Google Ads & Meta Ads Services",
    description: "Reach the right audience and generate qualified leads with targeted Google Ads and Meta Ads campaigns managed around your goals.",
  },
  "/services/social-media": {
    title: "Social Media Marketing Services",
    description: "Build your brand and grow your audience with strategic social media marketing, content planning and engaging creative campaigns.",
  },
  "/services/whatsapp-marketing": {
    title: "WhatsApp Marketing Services",
    description: "Connect with customers and grow your business through strategic WhatsApp marketing, broadcasts and customer messaging campaigns.",
  },
  "/services/web-and-app-development": {
    title: "Web & App Development Services",
    description: "Build fast, responsive websites and apps with development focused on better user experiences, conversions and business growth.",
  },
  "/services/graphic-design": {
    title: "Graphic Design Services",
    description: "Create a stronger visual identity with graphic design for brands, campaigns, social media and digital experiences that stand out.",
  },
  "/services/email-marketing": {
    title: "Email Marketing Services",
    description: "Nurture prospects and customers with strategic email marketing campaigns, useful content and automated customer journeys.",
  },
  "/portfolio": {
    title: "Digital Marketing & Creative Portfolio",
    description: "Explore selected SEO, advertising, branding, web development and digital marketing projects created for ambitious businesses.",
  },
  "/courses": {
    title: "Digital Marketing Courses",
    description: "Learn SEO, Google Ads, Meta Ads, social media and digital marketing through practical courses built from real industry experience.",
  },
  "/blog": {
    title: "Digital Marketing Insights",
    description: "Read practical insights on SEO, Google Ads, social media, digital marketing, web development and sustainable online growth.",
  },
  "/faq": {
    title: "Digital Marketing FAQs",
    description: "Find clear answers about digital marketing, SEO, advertising, web development, courses and working with Dorado9.",
  },
  "/contact": {
    title: "Contact a Digital Marketing Agency",
    description: "Get in touch with Dorado9 for SEO, digital marketing, advertising, web development and creative services tailored to your goals.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | Dorado9",
    description: "Read the Dorado9 privacy policy and learn how information is handled when you use our website.",
  },
  "/watch/dorado9-film-001": {
    title: "Dorado9 Film 001: Make Them Look Twice",
    description: "Watch Dorado9 Film 001, a visual story about strategy, creativity and digital growth for ambitious businesses.",
  },
};

function shortenAtWordBoundary(value, limit) {
  if (value.length <= limit) return value;
  const shortened = value.slice(0, limit).replace(/\s+\S*$/, "").trim();
  return shortened || value.slice(0, limit).trim();
}

export function optimizeMetaTitle(title) {
  const cleanTitle = (title || "Dorado9").replace(/\s+/g, " ").trim();
  return shortenAtWordBoundary(cleanTitle, META_TITLE_MAX_LENGTH);
}

export function optimizeMetaDescription(description) {
  if (!description) return "";

  const textContainer = document.createElement("div");
  textContainer.innerHTML = description;
  const cleanDescription = (textContainer.textContent || textContainer.innerText || "")
    .replace(/\s+/g, " ")
    .trim();

  return shortenAtWordBoundary(cleanDescription, META_DESCRIPTION_MAX_LENGTH);
}

function normalizePath(pathname) {
  if (pathname === "/") return "/";
  return `/${pathname.replace(/^\/+|\/+$/g, "")}`;
}

function titleFromSlug(slug) {
  return slug
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getRouteMetadata(pathname) {
  if (ROUTE_METADATA[pathname]) return ROUTE_METADATA[pathname];

  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "services" && segments[1]) {
    return {
      title: `${titleFromSlug(segments[1])} Services`,
      description: `Explore Dorado9 ${titleFromSlug(segments[1]).toLowerCase()} services for stronger digital growth.`,
    };
  }

  if (segments[0] === "portfolio" && segments[1]) {
    return {
      title: `${titleFromSlug(segments[1])} | Dorado9 Portfolio`,
      description: `Explore this Dorado9 digital marketing and creative project.`,
    };
  }

  if (segments[0] === "blog" && segments[1]) {
    return {
      title: "Digital Marketing Article | Dorado9",
      description: "Read practical digital marketing insights from Dorado9.",
    };
  }

  if (segments[0] === "courses" && segments[1]) {
    return {
      title: "Digital Marketing Course | Dorado9",
      description: "Explore practical digital marketing training from Dorado9.",
    };
  }

  return {};
}

function setCanonical(href) {
  const canonicals = Array.from(
    document.head.querySelectorAll('link[rel="canonical"]')
  );
  const canonical = canonicals[0] || document.createElement("link");

  canonicals.slice(1).forEach((element) => element.remove());
  canonical.setAttribute("rel", "canonical");
  canonical.setAttribute("href", href);
  document.head.appendChild(canonical);
}

function setRobots(content) {
  let robots = document.head.querySelector('meta[name="robots"]');

  if (!content) {
    robots?.remove();
    return;
  }

  if (!robots) {
    robots = document.createElement("meta");
    robots.setAttribute("name", "robots");
    document.head.appendChild(robots);
  }

  robots.setAttribute("content", content);
}

function setMetaDescription(content) {
  const descriptions = Array.from(
    document.head.querySelectorAll('meta[name="description"]')
  );
  let description = descriptions[0];

  descriptions.slice(1).forEach((element) => element.remove());

  if (!content) {
    description?.remove();
    return;
  }

  if (!description) {
    description = document.createElement("meta");
    description.setAttribute("name", "description");
    document.head.appendChild(description);
  }

  description.setAttribute("content", content);
}

function setOpenGraph(property, content) {
  const tags = Array.from(
    document.head.querySelectorAll(`meta[property="${property}"]`)
  );
  let tag = tags[0];

  tags.slice(1).forEach((element) => element.remove());

  if (!content) {
    tag?.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }

  tag.setAttribute("content", content);
}

export function JsonLd({ id, data }) {
  if (!data) return null;

  return (
    <script
      id={`schema-${id}`}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function pageLabel(pathname) {
  if (pathname === "/") return "Home";
  return pathname
    .split("/")
    .filter(Boolean)
    .map(titleFromSlug)
    .join(" · ");
}

function buildBaseSchema(pathname, isPublicPath) {
  if (!isPublicPath) return null;

  const url = `${SITE_URL}${pathname}`;
  const graph = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: getRouteMetadata(pathname).title || pageLabel(pathname),
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
    },
  ];

  if (pathname === "/") {
    graph.push(
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: SITE_URL,
        name: "Dorado9",
      },
      {
        "@type": "ProfessionalService",
        "@id": `${SITE_URL}/#organization`,
        name: "Dorado9",
        url: SITE_URL,
        logo: `${SITE_URL}/favicon.png`,
        email: "hello@dorado9.com",
        address: {
          "@type": "PostalAddress",
          addressLocality: "Amritsar",
          addressRegion: "Punjab",
          addressCountry: "IN",
        },
      }
    );
  }

  if (pathname !== "/" && pathname !== "/watch/dorado9-film-001") {
    const segments = pathname.split("/").filter(Boolean);
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        ...segments.map((segment, index) => ({
          "@type": "ListItem",
          position: index + 2,
          name: titleFromSlug(segment),
          item: `${SITE_URL}/${segments.slice(0, index + 1).join("/")}`,
        })),
      ],
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

export default function SEO({ canonical, noindex = false, title, description, schema = true }) {
  const { pathname } = useLocation();
  const normalizedPath = normalizePath(pathname);
  const isPublicPath = !pathname.startsWith("/admin");
  const baseSchema = schema
    ? buildBaseSchema(normalizedPath, isPublicPath && !noindex)
    : null;

  useEffect(() => {
    const shouldNoindex = noindex || pathname === "/admin" || !isPublicPath;
    const routeMetadata = getRouteMetadata(normalizedPath);
    const resolvedTitle = optimizeMetaTitle(title || routeMetadata.title || "Dorado9");
    const resolvedDescription = optimizeMetaDescription(
      description || routeMetadata.description || ""
    );

    document.title = resolvedTitle;
    setMetaDescription(resolvedDescription);
    setOpenGraph("og:title", resolvedTitle);
    setOpenGraph("og:description", resolvedDescription);

    if (shouldNoindex) {
      document.head.querySelectorAll('link[rel="canonical"]').forEach((element) => element.remove());
      setRobots("noindex,follow");
      return;
    }

    const requestedPath = canonical ? normalizePath(canonical) : normalizedPath;
    const canonicalUrl = `${SITE_URL}${requestedPath}`;
    setCanonical(canonicalUrl);
    setOpenGraph("og:url", canonicalUrl);
    setRobots(null);
  }, [canonical, description, isPublicPath, noindex, normalizedPath, pathname, title]);

  return <JsonLd id="base-page" data={baseSchema} />;
}
